import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Building2, Plus } from "lucide-react"
import { useEffect, useRef } from "react"

import { CreateOrganizationDialog } from "@/components/create-organization-dialog"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { logger } from "@/lib/logger"
import { useOrganization } from "@/store/organization"

/**
 * Organization selection screen
 * Shows all organizations the user is a member of
 */
export const Route = createFileRoute("/_authenticated/select-organization")({
  loader: () => {
    return {
      title: "Selecionar organização",
      path: "/select-organization",
    }
  },
  component: SelectOrganization,
})

function SelectOrganization() {
  const { organizations, loadOrganizations, isLoading, setCurrentOrg } =
    useOrganization()
  const navigate = useNavigate()
  const path = Route.useLoaderData()
  const { breadcrumbs } = Route.useRouteContext()
  const hasRequestedOrganizations = useRef(false)

  useEffect(() => {
    breadcrumbs.setCurrentPath(path)
  }, [breadcrumbs, path])

  useEffect(() => {
    if (
      organizations.length === 0 &&
      !isLoading &&
      !hasRequestedOrganizations.current
    ) {
      logger.info("No organizations in store, loading...")
      hasRequestedOrganizations.current = true
      loadOrganizations()
    }
  }, [organizations.length, isLoading, loadOrganizations])

  const handleSelectOrganization = (slug: string) => {
    logger.info("Navigating to organization", { slug })
    navigate({
      to: "/$orgSlug/dashboard",
      params: { orgSlug: slug },
    })
  }

  const handleCreateOrganization = async (orgId: string) => {
    await loadOrganizations()
    const { organizations: updatedOrgs } = useOrganization.getState()
    const newOrg = updatedOrgs.find((org) => org.id === orgId)

    if (newOrg) {
      setCurrentOrg(newOrg)
      handleSelectOrganization(newOrg.slug)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <Building2 className="size-12 mx-auto mb-4 text-muted-foreground animate-pulse" />
          <p className="text-muted-foreground">Carregando Organizações...</p>
        </div>
      </div>
    )
  }

  if (organizations.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <Building2 className="size-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">
            Nenhuma organização encontrada
          </p>
          <CreateOrganizationDialog
            trigger={
              <Button className="mt-4">
                <Plus className="mr-2 h-4 w-4" />
                Nova organização
              </Button>
            }
            onSuccess={handleCreateOrganization}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="container max-w-4xl mx-auto py-8">
      <div className="mb-8 flex flex-col items-center gap-4 text-center md:flex-row md:items-end md:justify-between md:text-left">
        <div>
          <h1 className="text-2xl font-bold mb-2 md:text-3xl">Selecionar organização</h1>
          <p className="text-muted-foreground">
            Escolha uma organização para continuar
          </p>
        </div>
        <CreateOrganizationDialog
          trigger={
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Nova organização
            </Button>
          }
          onSuccess={handleCreateOrganization}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {organizations.map((org) => {
          const names = org.name.split(" ")
          const orgInitials = names
            .map((n) => n[0])
            .join("")
            .toUpperCase()
            .slice(0, 2)

          return (
            <Card
              key={org.id}
              className="cursor-pointer transition-all hover:border-primary hover:shadow-md"
              onClick={() => handleSelectOrganization(org.slug)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <Avatar className="size-12 rounded-lg">
                    <AvatarImage
                      src={org.avatarUrl ?? undefined}
                      alt={org.name}
                    />
                    <AvatarFallback className="rounded-lg">
                      {orgInitials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg truncate">
                      {org.name}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {org.role === "admin" ? "Administrador" : "Membro"}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">
                  Clique para acessar esta organização
                </p>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
